import React from 'react';
import { MapPin } from 'lucide-react';
import { HERO } from '@/constants/testIds';
import { IMG } from '@/lib/assets';

export default function Hero() {
  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section
      id="inicio"
      data-testid={HERO.section}
      className="relative min-h-[100vh] w-full overflow-hidden bg-[#F5EFE0]"
    >
      {/* background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${IMG.heroPasture})`,
        }}
      />
      {/* cream overlay on left */}
      <div className="absolute inset-0 hero-bg" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 lg:pt-44 pb-24 grid lg:grid-cols-2 gap-10 items-center min-h-[100vh]">
        <div className="reveal max-w-2xl">
          <p className="font-cinzel text-[#0F3527]/80 text-sm tracking-[0.3em] mb-4">AGROPECUARIA</p>
          <h1 className="font-cormorant text-[#0F3527] text-[64px] sm:text-[88px] lg:text-[110px] leading-[0.95] font-medium tracking-tight">
            Tres Cerritos
          </h1>
          <div className="mt-4 mb-8 flex items-center gap-3">
            <span className="h-px w-12 bg-[#C9A361]" />
            <p className="font-cinzel text-[#A88340] text-[13px] tracking-[0.3em]">
              INNOVACIÓN QUE PRODUCE FUTURO
            </p>
          </div>

          <p className="font-cormorant italic text-[#0F3527] text-2xl mb-6">
            Genética. Tecnología. Legado.
          </p>

          <p className="text-[#0F3527]/85 text-base lg:text-lg leading-relaxed max-w-xl mb-10">
            Desde el Pacífico Norte de Costa Rica desarrollamos ganado Brangus, Sardo Negro y
            ganado comercial de alto desempeño, combinando mejoramiento genético, innovación
            tecnológica y una búsqueda constante de eficiencia para construir sistemas
            productivos cada vez mejores.
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              data-testid={HERO.ctaHistoria}
              onClick={() => go('nosotros')}
              className="btn-primary rounded-full px-7 py-3.5 text-sm tracking-[0.15em] font-cinzel uppercase"
            >
              Conozca Nuestra Historia
            </button>
            <button
              data-testid={HERO.ctaContacto}
              onClick={() => go('contacto')}
              className="btn-outline rounded-full px-7 py-3.5 text-sm tracking-[0.15em] font-cinzel uppercase"
            >
              Contáctenos
            </button>
          </div>
        </div>

        {/* location badge */}
        <div className="hidden lg:flex justify-end pr-4">
          <div className="bg-[#F5EFE0]/85 backdrop-blur-sm border border-[#C9A361]/40 rounded-sm px-5 py-3 flex items-center gap-3 shadow-sm">
            <span className="text-[#A88340]"><MapPin size={18} /></span>
            <p className="font-cormorant italic text-[#0F3527]">
              Desde el Pacífico Norte<br/>de Costa Rica
            </p>
          </div>
        </div>
      </div>

      {/* bottom pillars strip */}
      <PillarsBar />
    </section>
  );
}

function PillarsBar() {
  const items = [
    { icon: 'dna', label: 'MEJORAMIENTO\nGENÉTICO' },
    { icon: 'cpu', label: 'TECNOLOGÍA\nAPLICADA' },
    { icon: 'chart', label: 'EFICIENCIA\nPRODUCTIVA' },
    { icon: 'leaf', label: 'BIENESTAR ANIMAL\nY SOSTENIBILIDAD' },
    { icon: 'users', label: 'LEGADO PARA\nFUTURAS GENERACIONES' },
  ];
  return (
    <div className="absolute bottom-0 inset-x-0 bg-[#0F3527] text-[#F5EFE0]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-4">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-3">
            <PillarIcon name={it.icon} />
            <p className="font-cinzel text-[10.5px] sm:text-[11px] tracking-[0.18em] leading-[1.5]">
              {it.label.split('\n').map((l, idx) => (
                <span key={idx} className="block">{l}</span>
              ))}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function PillarIcon({ name }) {
  const stroke = '#C9A361';
  const common = { width: 26, height: 26, fill: 'none', stroke, strokeWidth: 1.5 };
  switch (name) {
    case 'dna':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M4 4c4 4 12 12 16 16M4 20c4-4 12-12 16-16M7 6h10M7 18h10M9 9h6M9 15h6" strokeLinecap="round" />
        </svg>
      );
    case 'cpu':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <rect x="6" y="6" width="12" height="12" rx="1" />
          <rect x="9" y="9" width="6" height="6" />
          <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" strokeLinecap="round" />
        </svg>
      );
    case 'chart':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M4 20h16M6 17V11M10 17V7M14 17v-8M18 17v-5" strokeLinecap="round" />
          <path d="M5 7l4-3 4 4 6-5" strokeLinecap="round" />
        </svg>
      );
    case 'leaf':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <path d="M20 4c0 8-6 14-14 14 0-8 6-14 14-14zM6 18c4-4 8-8 12-10" strokeLinecap="round" />
        </svg>
      );
    case 'users':
      return (
        <svg viewBox="0 0 24 24" {...common}>
          <circle cx="9" cy="8" r="3" />
          <circle cx="17" cy="9" r="2.5" />
          <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2.5 2-4 4-4s4 1.5 4 4" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}
